<template>
  <v-row class="landing-row">
    <v-col class="landing-col">
      <div class="logo">
        <img src="@/assets/img/closd_logo.svg" alt="Closd Logo"/>
      </div>
      <div class="landing-section">
        <img v-if="illustrationUrl" alt="" :src="illustrationUrl" />
        <div class="text-center">
          <h2 class="landing-title">{{ title }}</h2>
          <i18n-t v-if="isFullAccessLogin && !roomHadNdaSigned" :keypath="`sign.views.LandingPage.${textRedirect}`" tag="p" scope="global">
            <template #textSigningChecklist>
              <router-link
                  class="link-text"
                  :to="{ name: SIGNING_CHECKLIST_ROUTE_NAME, params: { mnemo, roomType: ROOM_ROUTE_TYPES.PROJECT } }"
              >
                  {{ $t('sign.views.LandingPage.textSigningChecklist') }}
              </router-link>
            </template>
            <template #redirectTime>
              <span>{{ redirectTime }}</span>
            </template>
          </i18n-t>
          <p v-if="roomHadNdaSigned"> {{ $t('sign.views.LandingPage.textNdaSigned') }} </p>
        </div>
      </div>
    </v-col>
    <v-col v-if="isShowDiscover" class="landing-col">
      <div class="landing-section landing-discover">
        <LawyerCarousel></LawyerCarousel>
        <div class="text-center">
          <h2 class="landing-title discover-title">{{ $t('sign.views.LandingPage.discover.title') }}</h2>
          <p class="discover-text mb-5">
            {{ $t('sign.views.LandingPage.discover.content') }}
          </p>
          <div class="discover-button d-flex justify-center pt-5">
            <AppButton
              type="primary"
              size="medium"
              variant="project"
              @click="openDemoClosd"
            >
              {{ $t('sign.views.LandingPage.discover.button') }}
            </AppButton>
          </div>
        </div>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'

import signedIllustrationUrl from '@/assets/img/landing-page/signed_illustration.svg'
import unsignedIllustrationUrl from '@/assets/img/landing-page/unsigned_illustration.svg'
import { DARK_BLUE_500 } from '@/common/utils/colors'
import { toast } from '@/common/utils/toast'
import AppButton from '@/design-system/buttons/AppButton.vue'
import { ROOM_ROUTE_TYPES, SIGNING_CHECKLIST_ROUTE_NAME, ROOM_ROUTE_NAME } from '@/router'
import { GET_ROOM_NDA_STATUS } from '@/store/modules/room/action_types'

import LawyerCarousel from './LawyerCarousel.vue'

const SUPPORTED_EVENTS = ['signing_complete', 'decline', 'cancel', 'nda_signed']
const NDA_SIGNED_STATUS = 'signed'
const NDA_POLLING_STATUS_DELAY = 3000

export default {
  name: 'LandingPage',
  components: { AppButton, LawyerCarousel },
  props: {
    event: {
      type: String,
      required: true,
      validator (value) {
        return SUPPORTED_EVENTS.includes(value)
      },
    },
    mnemo: {
      type: String,
      default: '',
    },
    isSubUser: {
      type: String,
      default: '',
    },
    usesSimpleSignature: {
      type: String,
      default: '',
    },
  },
  data () {
    return {
      redirectTime: 4,
      SIGNING_CHECKLIST_ROUTE_NAME,
      ROOM_ROUTE_TYPES,
      redirectId: 0,
      isShowDiscover: this.usesSimpleSignature === 'true' && this.isSubUser !== 'true',
      DARK_BLUE_500,
      redirectTimer: -1,
      ndaStatusPollingTimer: -1,
    }
  },
  head () {
    return {
      title: this.$t('sign.views.LandingPage.metaTitle'),
    }
  },
  computed: {
    ...mapGetters('login', ['isFullAccessLogin']),
    ...mapState('room', ['currentRoom']),

    roomHadNdaSigned () {
      return this.event === 'nda_signed'
    },
    illustrationUrl () {
      if (!SUPPORTED_EVENTS.includes(this.event)) return null
      return this.event === 'signing_complete' || this.roomHadNdaSigned ? signedIllustrationUrl : unsignedIllustrationUrl
    },
    title () {
      if (!SUPPORTED_EVENTS.includes(this.event)) return ''
      return this.$t('sign.views.LandingPage.simple.title.' + this.event)
    },
    textRedirect () {
      return this.isShowDiscover ? 'textRedirect' : 'textRedirectAutomatic'
    },
  },
  created () {
    if (this.isFullAccessLogin && !this.isShowDiscover && !this.roomHadNdaSigned) {
      this.redirectId = setTimeout(() => {
        this.$router.push({
          name: this.SIGNING_CHECKLIST_ROUTE_NAME,
          params: { mnemo: this.mnemo, roomType: ROOM_ROUTE_TYPES.PROJECT },
        })
      }, 6000)
    }
    if (this.roomHadNdaSigned) {
      this.ndaStatusPollingTimer = setTimeout(this.pollRoomNdaStatusLoop, NDA_POLLING_STATUS_DELAY)
    }
  },
  mounted () {
    this.startCountdown()
  },
  beforeUnmount () {
    clearTimeout(this.redirectId)
    clearInterval(this.redirectTimer)
    clearTimeout(this.ndaStatusPollingTimer)
    this.ndaStatusPollingTimer = -1
  },
  methods: {
    ...mapActions('room', [GET_ROOM_NDA_STATUS]),
    openDemoClosd () {
      window.location.href = 'https://www.lexisnexis.com/fr-fr/produits/closd-legal-project?utm_source=signpageclosd&utm_medium=invites-page-signature&utm_campaign=invites-page-signature'
    },
    startCountdown () {
      this.redirectTimer = setInterval(() => {
        if (this.redirectTime > 0) {
          this.redirectTime--
        } else {
          clearInterval(this.redirectTimer)
        }
      }, 1000)
    },
    async pollRoomNdaStatusLoop () {
      try {
        const { status } = await this.GET_ROOM_NDA_STATUS(this.mnemo)
        if (status === NDA_SIGNED_STATUS) {
          this.$router.push({
            name: ROOM_ROUTE_NAME,
            params: {
              mnemo: this.mnemo,
              roomType: ROOM_ROUTE_TYPES.DATAROOM,
            },
          })
        } else if (this.ndaStatusPollingTimer !== -1) {
          this.ndaStatusPollingTimer = setTimeout(this.pollRoomNdaStatusLoop, NDA_POLLING_STATUS_DELAY)
        }
      } catch {
        toast.error(this.$t('sign.views.LandingPage.ndaStatusError'))
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "vuetify/settings";

.logo {
  position: absolute;
  @media #{map-get($display-breakpoints, 'xs')}{
    position: relative;
    margin: 20px auto;
    border-bottom: 1px solid rgb(var(--v-theme-grey-100));
    padding-bottom: 20px;
    display: flex;
    justify-content: center;
  }
}
.link-text {
  text-decoration: none;
  color: v-bind(DARK_BLUE_500) !important;
}
.landing-row {
  min-height:100vh;
  background:white;
  padding: 30px 10px 10px 10px;
}
.landing-col {
  position: relative;
}
.landing-section {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  &.landing-discover {
      background: rgb(var(-v-theme-grey-25));
      border-radius: 24px;
      width: 100%;
      @media #{map-get($display-breakpoints, 'xs')}{
       max-width: 100vw;
     }
  }
  @media #{map-get($display-breakpoints, 'xs')}{
    height: auto;
  }
}
.landing-title {
  font-size: 24px !important;
  margin-top: 32px;
  margin-bottom: 12px;
  color: rgb(var(--v-theme-grey-900));
}
.discover-title {
  padding-left: 20px;
  padding-right: 20px;
  @media #{map-get($display-breakpoints, 'xs')}{
    margin-top: 0px;
    padding: 0px 20px;
  }
}
.discover-text {
  padding-left: 60px;
  padding-right: 60px;
  @media #{map-get($display-breakpoints, 'xs')}{
    font-size: 16px;
    padding-left: 20px;
    padding-right: 20px;
  }
}
.discover-button {
  margin-bottom: 40px;
}
</style>
